import type { CatalogApi } from '@backstage/catalog-client';
import type { Entity } from '@backstage/catalog-model';
import { DEFAULT_NAMESPACE, stringifyEntityRef } from '@backstage/catalog-model';

export const MY_GROUPS_PATH = '/my-groups';

const GITLAB_TEAM_PATH_ANNOTATION = 'gitlab.com/team-path';
const GITLAB_USER_TEAM_PATHS_ANNOTATION = 'gitlab.com/user-team-paths';

const POLL_MS = 5_000;
const RESOLVE_RETRY_MS = [1_500, 3_000, 5_000];

function sortGroups(groups: Entity[]): Entity[] {
  return [...groups].sort((a, b) =>
    (a.metadata.name ?? '').localeCompare(b.metadata.name ?? ''),
  );
}

function parseUserTeamPaths(user: Entity | undefined): string[] {
  const raw = user?.metadata.annotations?.[GITLAB_USER_TEAM_PATHS_ANNOTATION];
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map(path => String(path).trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

async function resolveGroupByMemberOfName(
  catalogApi: CatalogApi,
  name: string,
): Promise<Entity | undefined> {
  return catalogApi.getEntityByRef({
    kind: 'group',
    namespace: DEFAULT_NAMESPACE,
    name,
  });
}

async function resolveGroupByTeamPath(
  catalogApi: CatalogApi,
  teamPath: string,
): Promise<Entity | undefined> {
  const { items } = await catalogApi.getEntities({
    filter: {
      kind: 'group',
      [`metadata.annotations.${GITLAB_TEAM_PATH_ANNOTATION}`]: teamPath,
    },
    fields: ['kind', 'metadata', 'spec'],
  });
  return items[0];
}

async function resolveGroupsForUser(
  catalogApi: CatalogApi,
  user: Entity,
  memberOf: string[],
): Promise<Entity[]> {
  const teamPaths = parseUserTeamPaths(user);
  const entities = new Map<string, Entity>();

  await Promise.all(
    memberOf.map(async name => {
      const entity = await resolveGroupByMemberOfName(catalogApi, name);
      if (entity) {
        entities.set(stringifyEntityRef(entity), entity);
      }
    }),
  );

  if (entities.size < memberOf.length && teamPaths.length > 0) {
    const byPath = await Promise.all(
      teamPaths.map(path => resolveGroupByTeamPath(catalogApi, path)),
    );
    for (const entity of byPath) {
      if (entity) {
        entities.set(stringifyEntityRef(entity), entity);
      }
    }
  }

  return sortGroups([...entities.values()]);
}

/** Direct GitLab group memberships from the signed-in user's catalog User entity. */
export async function fetchMyGroups(
  catalogApi: CatalogApi,
  userEntityRef: string,
): Promise<{
  groups: Entity[];
  memberOfCount: number;
  /** Internal: names still not resolved (not shown in UI). */
  pendingGroupNames: string[];
}> {
  const user = await catalogApi.getEntityByRef(userEntityRef);
  const memberOf = (user?.spec?.memberOf as string[] | undefined) ?? [];

  if (memberOf.length === 0) {
    return { groups: [], memberOfCount: 0, pendingGroupNames: [] };
  }

  let groups = user ? await resolveGroupsForUser(catalogApi, user, memberOf) : [];

  for (const delayMs of RESOLVE_RETRY_MS) {
    if (groups.length >= memberOf.length || !user) {
      break;
    }
    await new Promise(resolve => setTimeout(resolve, delayMs));
    groups = await resolveGroupsForUser(catalogApi, user, memberOf);
  }

  const resolvedNames = new Set(groups.map(g => g.metadata.name));
  const pendingGroupNames =
    groups.length >= memberOf.length
      ? []
      : memberOf
          .filter(name => !resolvedNames.has(name))
          .sort((a, b) => a.localeCompare(b));

  return {
    groups,
    memberOfCount: memberOf.length,
    pendingGroupNames,
  };
}

/** Poll briefly while direct memberships may still be publishing after login. */
export function myGroupsPollIntervalMs(options: {
  groupCount: number;
  memberOfCount: number;
}): number | undefined {
  const { groupCount, memberOfCount } = options;
  if (memberOfCount > groupCount) {
    return POLL_MS;
  }
  if (groupCount === 0) {
    return POLL_MS;
  }
  return undefined;
}
